<template>
  <div class="flex flex-col overflow-hidden" :class="{ 'select-fixed-height': isFilterable }">
    <div class="row-center justify-between shrink-0 pl-4 py-2">
      <div class="text-s font-semibold color-text truncate min-w-0">
        {{ inputLabel || inputPlaceholder }}
      </div>
      <CustomIonButton fill="clear" size="small" color="danger" @onClick="onClose">
        <i class="ti ti-x text-l"></i>
      </CustomIonButton>
    </div>
    <div class="flex flex-col min-h-0 mb-3 mt-1">
      <CustomIonCheckbox
        v-if="isMultiSelect"
        ref="listRef"
        :inputId="inputId"
        :checkboxItems="dropdownItems"
        :initialValue="inputValue && Array.isArray(inputValue) ? inputValue : []"
        :isSearchable="isFilterable"
        :searchField="searchField"
        :isDisabled="isDisabled"
        :isVerticalMode="true"
        :hideSelectAll="false"
        :size="size"
        @onChange="onChangeSelectedValue"
      />
      <CustomIonRadioButton
        v-else
        ref="listRef"
        :inputId="inputId"
        :radioItems="dropdownItems"
        :initialValue="inputValue && !Array.isArray(inputValue) ? inputValue : undefined"
        :isSearchable="isFilterable"
        :searchField="searchField"
        :isVerticalMode="true"
        :isDisabled="isDisabled"
        :size="size"
        @onChange="onChangeSelectedValue"
      />
    </div>
    <div v-if="isMultiSelect" class="row-center justify-between shrink-0 px-3 py-2 bg-color-light">
      <CustomIonButton size="small" @onClick="onClose">Done</CustomIonButton>
    </div>
  </div>
</template>

<script lang="ts">
import CustomIonCheckbox from "@/components/inputs/CustomIonCheckbox.vue";
import CustomIonRadioButton from "@/components/inputs/CustomIonRadioButton.vue";
import CustomIonButton from "@/components/primitives/CustomIonButton.vue";
import type { ComponentSizeType } from "@/types/componentType";
import { InputOption } from "@/types/global";
import { IonContent } from "@ionic/vue";
import { PropType, computed, defineComponent, ref } from "vue";

export default defineComponent({
  name: "CustomIonSelectContent",
  components: { CustomIonButton, CustomIonCheckbox, CustomIonRadioButton, IonContent },
  props: {
    inputId: { type: String, required: true },
    inputLabel: { type: String, required: false },
    dropdownItems: { type: Array as PropType<Array<InputOption>>, required: true },
    initialValue: { type: [Number, Array<number | string>, String], default: null },
    inputPlaceholder: { type: String, required: false },
    isMultiSelect: { type: Boolean, default: false },
    isFilterable: { type: Boolean, default: false },
    searchField: { type: String, default: "value" },
    isDisabled: { type: Boolean, default: false },
    size: { type: String as PropType<ComponentSizeType>, default: "default" },
  },
  setup(props, { emit }) {
    const inputValue = ref<number | Array<number | string> | string | null>(props.initialValue);
    // Only one of the two list components renders (multi vs single), so a single ref covers both.
    const listRef = ref<
      InstanceType<typeof CustomIonCheckbox> | InstanceType<typeof CustomIonRadioButton> | null
    >(null);

    const selectedCount = computed(() =>
      Array.isArray(inputValue.value) ? inputValue.value.length : 0,
    );

    function onChangeSelectedValue(selectedValue: number | string | Array<number | string>) {
      if (props.isMultiSelect) {
        inputValue.value = selectedValue as Array<number | string>;
        return;
      }
      inputValue.value = selectedValue as number | string;
      emit("onChangeSelectedValue", inputValue.value);
    }

    function onClose() {
      if (props.isMultiSelect) {
        emit("onChangeSelectedValue", inputValue.value);
      }
      emit("onClose");
    }

    // Relayed from the owning overlay down to whichever list is rendered.
    async function focusSearch(): Promise<void> {
      await listRef.value?.focusSearch();
    }

    return { inputValue, listRef, selectedCount, onChangeSelectedValue, onClose, focusSearch };
  },
});
</script>
